// file path : src\services\user.services.ts

import { apiBaseUrl, appSubDir, debugMode } from "../config";
import { PlanResponse } from "../types/plans.type";
import User from "../types/user.type";
import { Browser } from '@capacitor/browser';
import { database } from "./sqlite.services";
import axios, { AxiosResponse } from "axios";
import { isPlatform } from "@ionic/react";
import { fcmUnsubscribeFromTopic } from "../functions/fcm.functions";


export function getLocalUserData() {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) as User : undefined
}
export function getProfile(token: string): Promise<AxiosResponse<User>> {
    return axios.get(`${apiBaseUrl}user`, {
        headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });
}
export class UserClass {
    user = {} as User
    constructor(user: User) {
        this.user = user
    }
    saveLocal() {
        window.localStorage.setItem('user', JSON.stringify(this.user))
    }
    static getLocal(): User | undefined {
        const data = window.localStorage.getItem('user')
        return data ? JSON.parse(data) as User : undefined
    }
    fetchFromOnline() {
        if (!this.user.token) {
            throw new Error("User Not logged in")
        }
        const myHeaders = new Headers();
        myHeaders.append("Accept", "application/json");
        myHeaders.append("Authorization", "Bearer " + this.user.token);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 seconds timeout

        const requestOptions = {
            method: 'GET',
            cache: "no-store",
            headers: myHeaders,
            redirect: 'follow',
            signal: controller.signal
        };
        return fetch(apiBaseUrl + "details", requestOptions as RequestInit).then((res) => res.json())
            .then((res: any) => {
                clearTimeout(timeoutId);
                if (res.message) {
                    throw new Error(res.message)
                }
                this.user = { ...this.user, ...res }
                this.saveLocal()
                return this.user
            })
            .catch((error) => {
                clearTimeout(timeoutId); // Ensure timeout is cleared in case of error
                if (error.name === 'AbortError') {
                    throw new Error("Request timed out");
                }
                throw error;
            })
    }
    goToPaymentLink() {
        if (!this.user.token) {
            return null
        }
        const myHeaders = new Headers();
        myHeaders.append("Authorization", "Bearer " + this.user.token);

        const requestOptions = {
            method: 'GET',
            cache: "no-store",
            headers: myHeaders,
            redirect: 'follow'
        };
        return fetch(apiBaseUrl + "payment-link", requestOptions as RequestInit).then((res) => res.text())
            .then((url: string) => {
                Browser.open({ url });
            })
    }

    getSubscriptionPlans() {
        if (!this.user.token) {
            throw new Error("Unautorized")
        }
        const myHeaders = new Headers();
        myHeaders.append("Authorization", "Bearer " + this.user.token);

        const requestOptions = {
            method: 'GET',
            cache: "no-store",
            headers: myHeaders,
            redirect: 'follow'
        };
        return fetch(apiBaseUrl + `payment/plans`, requestOptions as RequestInit).then((res) => (res.json() as Promise<PlanResponse>))
    }
    goToPlanPaymentLink(planId: number) {
        if (!this.user.token) {
            throw new Error("Unautorized")
        }
        const myHeaders = new Headers();
        myHeaders.append("Authorization", "Bearer " + this.user.token);

        const requestOptions = {
            method: 'GET',
            cache: "no-store",
            headers: myHeaders,
            redirect: 'follow'
        };
        let endPoint = apiBaseUrl + `payment/plans/${planId}/url`
        if (!isPlatform('capacitor')) {
            const { protocol, host } = window.location
            endPoint += `?ru=` + encodeURIComponent(`${protocol}//${host}${appSubDir}`)
        }
        return fetch(endPoint, requestOptions as RequestInit).then((res) => res.json())
            .then((res: {
                success: boolean
                payment_url: string
            }) => {
                if (!res.success || !res.payment_url) {
                    throw new Error("Failed to get payment url")
                }
                if (isPlatform('capacitor')) {
                    Browser.open({
                        url: res.payment_url
                    });
                } else {
                    window.location.href = res.payment_url
                }
            })
    }
    days() {
        const date = new Date(Date.parse(this.user.expires_at)) as any
        const now = new Date() as any;
        const diffTime = Math.abs(date - now);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        // console.log(diffTime + " milliseconds");
        // console.log(diffDays + " days");
        return diffDays
    }
    logOut() {
        const user = this.user
        if (user.token) {
            const myHeaders = new Headers();
            myHeaders.append("Authorization", "Bearer " + user?.token);
            myHeaders.append("Accept", "application/json");

            const requestOptions = {
                method: 'GET',
                headers: myHeaders,
                redirect: 'follow'
            };

            return fetch(apiBaseUrl + "logout", requestOptions as RequestInit)
                .then(response => response.text())
                .then(result => debugMode && console.log(result))
                .catch(error => console.error('error', error))
                .finally(() => {
                    window.localStorage.removeItem('user')
                    if (user) user.token = ""
                    return afterLogout()
                })
        } else {
            return afterLogout()
        }
    }
}
export function isPaid(user: User) {
    if (user.expires_at) {
        return (Date.parse(user.expires_at) > (new Date()).getTime())
    } else {
        return false
    }
}
export async function afterLogout() {
    if (isPlatform('capacitor')) {
        fcmUnsubscribeFromTopic('paid-user')
        fcmUnsubscribeFromTopic('free-user')
        await database.clean()
    }
    window.localStorage.clear()
}
export function updateUserDetails(user: Partial<User>, token: string) {
    return axios.post(apiBaseUrl + "details", { ...user }, { headers: { "Authorization": "Bearer " + token } })
}